
import React from 'react';
import { Loader2, CheckSquare, AlertTriangle, Check, ChefHat, ScanEye, CheckCircle2, Printer, MessageCircle } from 'lucide-react';
import { Order, SystemConfig } from '../../types';
import { isScheduledRoutingEligible } from '../../services/logistics/core/scheduled';

interface ActionsProps {
    order: Order;
    systemConfig?: SystemConfig;
    isAccepting: boolean;
    isConfirmingHeldReady: boolean;
    onConfirmClick: (e: React.MouseEvent) => void;
    onReadyToggle: (e: React.MouseEvent) => void;
    onTrackOrder?: (id: string) => void;
    onManualComplete?: (id: string) => void;
    onPrint?: (e: React.MouseEvent) => void;
    onOpenCommunication?: (order: Order, rect: DOMRect) => void;
    compact?: boolean;
    isSimpleView: boolean;
    canPrint?: boolean;
    ultraCompact?: boolean; // v60.0: Modo ultra-compacto para telas pequenas
}

/**
 * v41.13: OrderActions - UI Standardization & Logic Fixes
 * - Altura padronizada (h-7)
 * - Botão Pronto substituído por Entregue
 * - "CONCLUIR" -> "ENTREGUE"
 */
export const OrderActions: React.FC<ActionsProps> = ({
    order, isAccepting, isConfirmingHeldReady, onConfirmClick, onReadyToggle,
    onTrackOrder, onManualComplete, onPrint, onOpenCommunication, compact, isSimpleView, canPrint, systemConfig,
    ultraCompact = false
}) => {

    const isPending = order.status === 'pending' || order.status === 'waiting_confirmation';
    const isConfirmed = order.status === 'confirmed' || order.status === 'scheduled_confirmed';
    const isReady = order.isReady;
    const isTakeout = order.type === 'TAKEOUT';
    const isThirdPartyDispatched = order.status === 'dispatched_3rd_party';
    const scheduledLeadMinutes = systemConfig?.cardapioWeb?.scheduledRoutingLeadMinutes ?? 45;
    const isScheduledLocked = !isScheduledRoutingEligible(order, scheduledLeadMinutes);

    // Detect if order needs confirmation (pending status from external API or online payment)
    const needsConfirmation = order.status === 'waiting_confirmation' || order.status === 'payment_pending' || order.status === 'pending' || !!order.needsConfirmation;

    // v41.13: Permitir concluir manualmente pedidos Takeout (Prontos) ou Envios Externos (Despachados)
    // v56.9: Force visibility for Takeout Ready to ensure it doesn't look "stuck"
    const canShowCompleteButton = ((isTakeout && isReady) || isThirdPartyDispatched) && !!onManualComplete;

    // v41.13: Hide Ready button if Complete button is visible (Substituição)
    // v50.1: Hide Ready button if Accept button is visible (needsConfirmation)
    // v57.10: Ensure Ready button stays visible when order IS ready (toggle behavior)
    const canShowReadyButton = (isPending || isConfirmed || isReady) && !needsConfirmation && !order.isLoading && !canShowCompleteButton && !isScheduledLocked;

    // v60.0: Classes adaptativas para modo ultra-compacto - versões maiores para botões de status
    // Botões de status (ACEITAR, PRONTO, ENTREGUE) precisam ser maiores e mais legíveis
    const btnBase = ultraCompact 
        ? "flex items-center gap-1 px-2 py-1 rounded border shadow-sm transition-all active:scale-95 group shrink-0 h-7"
        : "flex items-center gap-1.5 px-3 rounded-lg border shadow-sm transition-all active:scale-95 group shrink-0 h-7";
    const iconBase = ultraCompact ? "w-3 h-3" : "w-3.5 h-3.5";
    const textBase = ultraCompact 
        ? "text-[10px] font-black uppercase tracking-wider leading-none" 
        : "text-xs font-black uppercase tracking-wider leading-none";
    const actionBtnSize = ultraCompact ? "h-7 w-7" : "h-7 w-7";
    const actionIconSize = ultraCompact ? "w-3.5 h-3.5" : "w-4 h-4";

    return (
        <div className={`flex items-center pointer-events-auto shrink-0 ${ultraCompact ? 'gap-0.5' : 'gap-1'}`}>

            {!isTakeout && onTrackOrder && !isSimpleView && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onTrackOrder(order.id); }}
                    className={`rounded transition-colors uppercase flex items-center justify-center gap-1 font-black ${ultraCompact ? 'h-6 w-6 px-0' : 'h-7 px-2'} border ${compact ? 'bg-sal-cyber-teal/5 text-sal-cyber-teal hover:bg-sal-cyber-teal/10 border-sal-cyber-teal/20' : 'text-xs text-sal-cyber-teal hover:text-sal-cyber-teal/80 border-sal-cyber-teal/20'}`}
                    title="S.A.L. RECON: Abrir Street View"
                    aria-label="S.A.L. RECON: Abrir Street View"
                >
                    <ScanEye className={actionIconSize} />
                    {!ultraCompact && !compact && <span>RECON</span>}
                </button>
            )}

            {/* Manual Print Button */}
            {canPrint && onPrint && !needsConfirmation && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onPrint(e); }}
                    className={`flex items-center justify-center gap-1 border rounded transition-colors ${actionBtnSize} ${compact ? 'bg-card-bg border-card-border text-card-text-muted' : 'bg-card-bg text-card-text-muted hover:text-card-text border-card-border hover:bg-app-bg'}`}
                    title="Imprimir Cupom"
                    aria-label="Imprimir Cupom"
                >
                    <Printer className={actionIconSize} />
                </button>
            )}

            {/* WhatsApp Communication Button */}
            {onOpenCommunication && order.phone && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        onOpenCommunication(order, rect);
                    }}
                    className={`flex items-center justify-center gap-1 border rounded transition-colors ${actionBtnSize} ${compact ? 'bg-status-ready/5 border-status-ready/20 text-status-ready hover:bg-status-ready/10' : 'bg-status-ready/5 text-status-ready hover:bg-status-ready/10 border-status-ready/20'}`}
                    title="Abrir Comunicação"
                    aria-label="Abrir Comunicação"
                >
                    <MessageCircle className={`${actionIconSize} text-status-ready`} />
                </button>
            )}

            {needsConfirmation && !order.isLoading && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onConfirmClick(e); }}
                    disabled={isAccepting}
                    className={`${btnBase} bg-sal-burnt-orange border-sal-burnt-orange text-white shadow-[0_0_15px_rgba(249,115,22,0.4)] hover:brightness-110`}
                >
                    {isAccepting ? <Loader2 className={`${iconBase} animate-spin`} /> : <CheckSquare className={`${iconBase} font-bold`} />}
                    {!ultraCompact && <span className={textBase}>ACEITAR</span>}
                </button>
            )}

            {canShowReadyButton && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onReadyToggle(e); }}
                    className={`${btnBase} ${isConfirmingHeldReady
                        ? 'bg-status-warning border-status-warning text-white font-black shadow-[0_0_15px_rgba(245,158,11,0.4)]'
                        : isReady
                            ? 'bg-status-ready border-status-ready text-white shadow-[0_0_15px_rgba(16,185,129,0.4)] scale-105 font-bold'
                            : 'bg-status-ready/10 border-status-ready/30 text-status-ready hover:bg-status-ready hover:text-white hover:border-status-ready font-medium'
                        }`}
                >
                    {!ultraCompact && !compact && (
                        isConfirmingHeldReady ? <AlertTriangle className={iconBase} /> : (isReady ? <Check className={`${iconBase} font-bold`} /> : <ChefHat className={iconBase} />)
                    )}
                    <span className={textBase}>
                        {isConfirmingHeldReady ? 'LIBERAR?' : (isReady ? (compact ? 'PRONTO' : 'PRONTO!') : (compact ? 'PRONTO?' : 'PRONTO?'))}
                    </span>
                </button>
            )}

            {canShowCompleteButton && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onManualComplete(order.id); }}
                    className={`${btnBase} bg-status-ready text-white border border-status-ready shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:brightness-110`}
                >
                    <CheckCircle2 className={iconBase} />
                    {!ultraCompact && <span className={textBase}>ENTREGUE</span>}
                    {ultraCompact && <span className={textBase}>OK</span>}
                </button>
            )}
        </div>
    );
};
